import "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import markerIcon from "../../assets/icons/marker.svg";
import clusterIcon from "../../assets/icons/cluster.svg";
import { isEmpty } from "../../utils/functions";
import { AntPath, antPath } from "leaflet-ant-path";
import { t } from "../../utils/translations";

let map = null;
let markerGroup = null;
let allMarkers = [];
let polyLines = [];
let center = null;
let zoomLevel = null;
let currentAnimation = null;
let duration = 2; // seconds
const defaultZoom = 14;

const initMap = (isMobile) => {
  const initalPosition = isMobile ? [52.0, 8.0] : [52.092876, 5.10448];
  const initalZoom = 4;

  /* Handle map. */
  map = L.map("map", {
    dragging: true,
    scrollWheelZoom: false,
    zoomControl: true, // active for test purposes
    doubleClickZoom: undefined,
  }).setView(initalPosition, initalZoom);

  L.tileLayer(
    "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}",
    //"https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png",
    {
      attribution:
        'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
      maxZoom: 18,
      id: `${process.env.MAPBOX_ID}`,
      tileSize: 512,
      zoomOffset: -1,
      scrollWheelZoom: false,
      accessToken: process.env.LEAFLET_TOKEN,
    }
  ).addTo(map);

  map.on("movestart", function (e) {
    let bounds = map.getBounds();
    for (let marker of allMarkers) {
      if (!bounds.contains(marker.getLatLng()) && !map.hasLayer(marker)) {
        marker.closePopup();
      } else if (!bounds.contains(marker.getLatLng())) {
        let parent = markerGroup.getVisibleParent(marker);
        parent.closePopup();
      }
    }
  });

  map.on("moveend", function (e) {
    setTimeout(() => {
      let bounds = map.getBounds();
      for (let marker of allMarkers) {
        if (bounds.contains(marker.getLatLng()) && map.hasLayer(marker)) {
          marker.openPopup();
        } else if (bounds.contains(marker.getLatLng())) {
          let parent = markerGroup.getVisibleParent(marker);
          // TODO: Add English translation.
          if (parent.getPopup() === undefined) {
            let clusterPopup =
              "<h5>" +
              t("click_to_open") +
              "</h5>" +
              "<h4>" +
              parent._childCount +
              " " +
              t("events") +
              "</h4>";
            parent
              .bindPopup(clusterPopup, {
                autoClose: false,
                autoPan: false,
                closeOnClick: false,
                className: "cluster-popup",
              })
              .openPopup();

            L.DomEvent.on(parent._popup._contentNode, "click", function (e) {
              parent._icon.click();
              parent.closePopup();
            });
          } else {
            parent.openPopup();
          }
        }
      }
    }, 400);
  });

  // Fly map to Ukraine, so user gets more reference where they are and where they are going.
  if (isMobile) {
    setTimeout(() => {
      map.flyToBounds(
        [
          [48.8532, 39.307815],
          [48.015883, 37.6053],
        ],
        {
          maxZoom: 6,
          padding: [0, 180],
          duration: 2.5,
        }
      );
    }, 1000);
  }
};

// This is supposed to prevent missing tiles when using the browser history navigation.
// If tiles are still missing, reconsider this function. #20935
export function reInitMap() {
  setTimeout(() => {
    if (map !== null) {
      map.invalidateSize();
    }
  }, 0);
}

function updateMarkers(
  markers,
  zoomOverwrite,
  centerOverwrite,
  paragraphID,
  isMobile
) {
  // Clear all current markers if available
  allMarkers = [];
  if (markerGroup !== null) {
    map.removeLayer(markerGroup);
  }

  let defaultIcon = L.icon({
    iconUrl: markerIcon,
    iconSize: [20, 20],
  });

  let areaIcon = L.icon({
    iconUrl: markerIcon,
    iconSize: [60, 60],
  });

  let icons = {
    exact: defaultIcon,
    area: areaIcon,
    route: defaultIcon,
    point: defaultIcon,
  };

  if (map !== null) {
    markerGroup = new L.markerClusterGroup({
      iconCreateFunction: function (cluster) {
        return L.divIcon({
          iconUrl: clusterIcon,
          className: "cluster-icon",
          iconSize: [40, 40],
          html: "<b>" + cluster.getChildCount() + "</b>",
        });
      },
    });

    for (let marker of markers) {
      // Only add marker if geo position is known.
      // TODO: Find out if this is the proper method and place to do this.
      // Added to fix errors thrown by markers without location (which do exist).
      if (marker.marker_data.lat && marker.marker_data.lng) {
        let _marker = L.marker(
          [marker.marker_data.lat, marker.marker_data.lng],
          {
            icon: icons[marker.marker_type],
            id: marker.marker_data.id,
            type: marker.marker_type,
          }
        );

        markerGroup.addLayer(_marker);

        allMarkers.push(_marker);
        if (marker.marker_type !== "point") {
          _marker.bindPopup(createPopup(marker.marker_data), {
            autoClose: false,
            autoPan: false,
            closeOnClick: false,
            className:
              "cat-" + marker.marker_data.type_icon + " animate-popup ",
          });
        }
      }
    }

    map.addLayer(markerGroup);
    bindMarkers();
    // Plot markers on map and zoom to position
    if (allMarkers.length) {
      let markerBounds = markerGroup.getBounds();
      let latLngBound = new L.LatLngBounds(
        markerBounds._northEast,
        markerBounds._southWest
      );

      let defaultCenter = latLngBound.getCenter();
      zoomLevel = zoomOverwrite ? parseInt(zoomOverwrite) : defaultZoom;
      center = centerOverwrite ? centerOverwrite : defaultCenter;

      if (paragraphID === "deel_1_hoofdstuk_1_paragraaf_1_1" && !isMobile) {
        setTimeout(() => {
          map.flyToBounds(markerBounds, {
            animate: true,
            maxZoom: zoomLevel,
            padding: [0, 180],
            duration: 2.5,
          });
        }, 1000);
      } else {
        map.flyToBounds(markerBounds, {
          animate: true,
          maxZoom: zoomLevel,
          padding: [0, 180],
          duration: duration,
        });
      }
    } else {
      if (centerOverwrite?.lat && centerOverwrite?.lng) {
        zoomLevel = zoomOverwrite ? zoomOverwrite : defaultZoom;
        map.flyTo([centerOverwrite.lat, centerOverwrite.lng], zoomLevel, {
          animate: true,
          duration: duration,
        });
      }
    }
  }
}

function createPopup(markerData) {
  const description =
    markerData.description !== "" && markerData.description !== null
      ? markerData.description
      : false;
  const datetime =
    markerData.datetime !== "" && markerData.datetime !== null
      ? "<time>" + markerData.datetime + "</time>"
      : false;

  if (description && datetime) {
    return `<h5>${description} | ${datetime}</h5><h4>${markerData.title}</h4>`;
  } else if (description && !datetime) {
    return `<h5>${description}</h5><h4>${markerData.title}</h4>`;
  } else if (!description && datetime) {
    return `<h5>${datetime}</h5><h4>${markerData.title}</h4>`;
  } else if (!description && !datetime) {
    return `<h4>${markerData.title}</h4>`;
  } else {
    return ``;
  }
}

function bindMarkers() {
  let markerLinks = document.querySelectorAll("[data-related-marker]");

  for (let markerLink of markerLinks) {
    markerLink.onclick = function (e) {
      e.preventDefault();
    };
    markerLink.addEventListener("mouseenter", animatePopup);
    markerLink.addEventListener("mouseleave", removeAnimation);
  }

  // Remove previously created back buttons
  removeBackButton();
  markerGroup.on("clusterclick", addBackButton);
}

function animatePopup(e) {
  let currentMarkerId = e.target.getAttribute("data-related-marker");
  for (let marker of allMarkers) {
    if (marker.options.id === currentMarkerId) {
      if (map.hasLayer(marker)) {
        if (marker.options.type !== "point") {
          let container = marker._popup._container;
          let style = window.getComputedStyle(container);
          let matrix = new WebKitCSSMatrix(style.transform);
          let translateX = matrix.m41;
          let translateY = matrix.m42;

          currentAnimation = container.animate(
            [
              // keyframes
              {
                transform:
                  "translate(" + translateX + "px," + translateY + "px)",
              },
              {
                transform:
                  "translate(" + translateX + "px," + (translateY - 10) + "px)",
              },
              {
                transform:
                  "translate(" + translateX + "px," + translateY + "px)",
              },
            ],
            {
              // timing options
              duration: 1000,
              iterations: Infinity,
            }
          );
        } else {
          let style = window.getComputedStyle(marker._icon);
          let matrix = new WebKitCSSMatrix(style.transform);
          let translateX = matrix.m41;
          let translateY = matrix.m42;

          marker.setIcon(
            L.icon({
              iconUrl: markerIcon,
              iconSize: [20, 20],
              className: "point-icon",
            })
          );

          currentAnimation = marker._icon.animate(
            [
              // keyframes
              {
                transform:
                  "translate(" +
                  translateX +
                  "px," +
                  translateY +
                  "px) scale(1, 1)",
              },
              {
                transform:
                  "translate(" +
                  translateX +
                  "px," +
                  translateY +
                  "px) scale(2.5, 2.5)",
              },
              {
                transform:
                  "translate(" +
                  translateX +
                  "px," +
                  translateY +
                  "px) scale(1, 1)",
              },
            ],
            {
              // timing options
              duration: 1000,
              iterations: Infinity,
            }
          );
        }
      } else {
        let container = markerGroup
          .getVisibleParent(marker)
          .getPopup()._container;
        let style = window.getComputedStyle(container);
        let matrix = new WebKitCSSMatrix(style.transform);
        let translateX = matrix.m41;
        let translateY = matrix.m42;

        currentAnimation = container.animate(
          [
            // keyframes
            {
              transform: "translate(" + translateX + "px," + translateY + "px)",
            },
            {
              transform:
                "translate(" + translateX + "px," + (translateY - 10) + "px)",
            },
            {
              transform: "translate(" + translateX + "px," + translateY + "px)",
            },
          ],
          {
            // timing options
            duration: 1000,
            iterations: Infinity,
          }
        );
      }
    }
  }
}

function removeAnimation() {
  if (!!currentAnimation) {
    currentAnimation.cancel();
  }
}

function addBackButton() {
  // TODO: Button tekst vertalen.
  let button = `<button id='back-button-cluster'><span>${t(
    "return_to_overview"
  )}</span></button>`;
  let body = document.getElementById("top");
  body.insertAdjacentHTML("afterbegin", button);

  let createdButton = document.getElementById("back-button-cluster");
  createdButton.addEventListener("click", backToOverview);
}

function removeBackButton() {
  let backButton = document.getElementById("back-button-cluster");
  if (backButton) {
    backButton.remove();
  }
}

function backToOverview() {
  let markerBounds = markerGroup.getBounds();
  map.flyToBounds(markerBounds, {
    maxZoom: zoomLevel,
    padding: [0, 180],
  });
  let backButton = document.getElementById("back-button-cluster");
  backButton.remove();
}

export const drawPolyLine = (
  paragraphs,
  currParagraphID,
  polylineData,
  index
) => {
  // Remove redundant lines
  clearMapLines(map);

  const polylines = [];
  const settings = {
    color: "#fbb03b",
    weight: 5,
    opacity: 0.5,
    smoothFactor: 1,
  };

  // Construct previous polyline, without currentPolyLine
  for (let paragraph in paragraphs) {
    if (paragraphs[paragraph].id === currParagraphID) {
      break;
    }
    if (!isEmpty(paragraphs[paragraph].polylines)) {
      polylines.push(paragraphs[paragraph].polylines);
    }
  }
  if (polylines.length) {
    for (let prevPolyline in polylines) {
      const prevSettings = settings;
      if (index >= 104 && polylines[prevPolyline].hide_later) {
        prevSettings.opacity = 0;
      } else {
        prevSettings.opacity = 0.5;
      }

      if (polylines[prevPolyline].is_dashed) {
        prevSettings.dashArray = 20;
      } else {
        prevSettings.dashArray = 0;
      }
      if (polylines[prevPolyline].is_return_route) {
        prevSettings.color = "#f24444";
      }

      const prevLine = new L.Polyline(
        [...polylines[prevPolyline].coordinates],
        prevSettings
      );
      prevLine.addTo(map);
    }
  }

  // Construct currentPolyLine
  if (polylineData.coordinates.length) {
    let line;

    if (polylineData.is_return_route) {
      settings.color = "#f24444";
    }

    if (polylineData.is_dashed) {
      settings.dashArray = 20;
      line = new L.Polyline.AntPath([...polylineData.coordinates], settings);
      line.addTo(map);
    } else {
      line = new L.Polyline([...polylineData.coordinates], settings);
      line.addTo(map);
    }
  }
};

function clearMapLines(map) {
  for (let i in map._layers) {
    if (map._layers[i]._path !== undefined) {
      try {
        map.removeLayer(map._layers[i]);
      } catch (e) {
        console.log("problem with " + e + map._layers[i]);
      }
    }
  }
}

// export default init;
export { initMap, updateMarkers };
